import { STATUS_OPTIONS } from "../../../shared/utils/utils";
import {
  buildStatusObject,
  getMarkAsMenuOptions,
  patchClient,
  resolveStatusPresentation,
} from "../utils/collectionRowUtils";
import { toast } from "react-toastify";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CollectionStatusIcon } from "../../../ui/components/CollectionStatusIcons";
import { NotesIcon } from "../../../ui/components/MonitoringMetricIcons";
import {
  ActivityCharacterizeIcon,
  ActivityCompleteInfoIcon,
  ActivityContainerSettingsIcon,
  ActivityShareDownloadIcon,
  ActivityShareEmailIcon,
  ActivityTagIcon,
  ActivityUploadFileIcon,
} from "./ActivityShareExportItems";

const MENU_WIDTH_PX = 240;
const STATUS_DROPDOWN_WIDTH_PX = 200;
const MENU_ITEM_CLASS = "char-dropdown-item";
const MENU_SECTION_CLASS =
  "px-3.5 pt-2 pb-1 text-xs font-semibold text-[#222222]";
const MENU_DIVIDER_CLASS = "char-dropdown-item-divider";

const StatusV2Picker = ({
  statusPresentation,
  menuOptions,
  onChange,
  disabled = false,
  saving = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    function onDocumentClick(event) {
      if (!open) return;
      if (
        !buttonRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    function onKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const updateMenuPosition = useCallback(() => {
    if (!open || !buttonRef.current || !menuRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = STATUS_DROPDOWN_WIDTH_PX;
    const maxLeft = window.innerWidth - menuWidth - 8;
    const left = Math.min(Math.max(8, rect.left), maxLeft);

    menuRef.current.style.top = `${rect.bottom + 4}px`;
    menuRef.current.style.left = `${left}px`;
    menuRef.current.style.width = `${menuWidth}px`;
    menuRef.current.style.position = "fixed";
    menuRef.current.style.visibility = "visible";
  }, [open]);

  useLayoutEffect(() => {
    updateMenuPosition();
  }, [updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handleReposition = () => updateMenuPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, updateMenuPosition]);

  const dropdownMenu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="menu"
        className="char-dropdown char-dropdown--compact fixed z-[10000]"
        style={{ visibility: "hidden", right: "auto", marginTop: 0 }}
      >
        <div className={MENU_SECTION_CLASS}>{t("Marked as")}</div>
        <div className="pb-0.5">
          {menuOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onChange(option.value);
              }}
              className={MENU_ITEM_CLASS}
            >
              <span className="text-[#222222]">
                <CollectionStatusIcon statusIcon={option.statusIcon} />
              </span>
              <span className="whitespace-nowrap">{option.label}</span>
            </button>
          ))}
        </div>
      </div>,
      document.body,
    );

  return (
    <div className="relative inline-flex shrink-0">
      <button
        ref={buttonRef}
        type="button"
        aria-label={t("Status")}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled || saving}
        onClick={() => setOpen((previous) => !previous)}
        style={{
          backgroundColor: statusPresentation.pillBackground,
          color: statusPresentation.pillTextColor,
        }}
        className={`inline-flex h-[30px] items-center justify-center gap-1 rounded-full border-0 px-3 py-1 text-sm font-medium transition-opacity ${
          disabled || saving
            ? "cursor-not-allowed opacity-50"
            : "cursor-pointer hover:opacity-90"
        }`}
      >
        {saving ? (
          <i
            className="fas fa-spinner fa-spin text-gray-500"
            aria-hidden="true"
          />
        ) : (
          <>
            <CollectionStatusIcon statusIcon={statusPresentation.statusIcon} />
            <span className="whitespace-nowrap">
              {statusPresentation.label}
            </span>
          </>
        )}
      </button>
      {dropdownMenu}
    </div>
  );
};

/**
 * Composant pour l'en-tête d'une ligne de collection
 * Affiche le titre, le statut et les boutons d'action
 */
const CollectionRowHeader = ({
  wasteName,
  timeLine,
  checkStatus,
  setCheckStatus,
  rawCheckStatus,
  setRawCheckStatus,
  onStatusSaved,
  savingStatus,
  setSavingStatus,
  loading,
  superContainers,
  imageData,
  azureUrl,
  t,
  onOpenCollectionInfo,
  onOpenTags,
  onOpenFiles,
  onOpenNotes,
  onOpenDetail,
  onOpenCharacterize,
  toCharacterization = false,
  canCharacterize = true,
  characterizeBlockedReason = null,
  onOpenContainerSettings,
  onRequestPdf,
  onRequestEmail,
  onRequestCsv,
}) => {
  const handleStatusChange = async (nextValue) => {
    const prev = checkStatus;
    const prevRaw = rawCheckStatus;
    const next = buildStatusObject(nextValue, t);
    setCheckStatus(next);
    setRawCheckStatus(next);
    setSavingStatus(true);

    try {
      await patchClient({
        timeLine,
        superContainers,
        imageData,
        azureUrl,
        patch: { checkStatus: next },
      });
      await onStatusSaved?.();
    } catch (err) {
      setCheckStatus(prev);
      setRawCheckStatus(prevRaw);
      toast.error(t("Unable to update status"));
      console.error("patchClient error:", err);
    } finally {
      setSavingStatus(false);
    }
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    function onDocumentClick(event) {
      if (!isMenuOpen) return;
      if (
        !menuRef.current?.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    }

    function onKeyDown(event) {
      if (event.key === "Escape") setIsMenuOpen(false);
    }

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isMenuOpen]);

  const updateMenuPosition = useCallback(() => {
    if (!isMenuOpen || !buttonRef.current || !menuRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = MENU_WIDTH_PX;
    const maxLeft = window.innerWidth - menuWidth - 8;
    const left = Math.min(Math.max(8, rect.right - menuWidth), maxLeft);

    const menuHeight = menuRef.current.offsetHeight || 0;
    const defaultTop = rect.bottom + 8;
    const fitsBelow = defaultTop + menuHeight <= window.innerHeight - 8;
    const top = fitsBelow ? defaultTop : Math.max(8, rect.top - 8 - menuHeight);

    menuRef.current.style.top = `${top}px`;
    menuRef.current.style.left = `${left}px`;
    menuRef.current.style.width = `${menuWidth}px`;
    menuRef.current.style.maxWidth = `${menuWidth}px`;
    menuRef.current.style.position = "fixed";
    menuRef.current.style.visibility = "visible";
  }, [isMenuOpen]);

  useLayoutEffect(() => {
    updateMenuPosition();
  }, [updateMenuPosition]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handleReposition = () => updateMenuPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isMenuOpen, updateMenuPosition]);

  const isDisabled = loading;
  const menuButtonClass = isDisabled
    ? "bg-[#D0D0D0] cursor-not-allowed"
    : "bg-white hover:bg-gray-50 cursor-pointer";

  const statusPresentation = resolveStatusPresentation(
    checkStatus,
    t,
    rawCheckStatus,
  );
  const statusOptions = useMemo(() => STATUS_OPTIONS(t), [t]);
  const markAsItems = useMemo(
    () => getMarkAsMenuOptions(statusOptions, statusPresentation.key, t),
    [statusOptions, statusPresentation.key, t],
  );

  const dropdownMenu =
    isMenuOpen &&
    createPortal(
      <div
        ref={menuRef}
        role="menu"
        className="char-dropdown char-dropdown--compact fixed z-[9999]"
        style={{ visibility: "hidden", right: "auto", marginTop: 0 }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={MENU_ITEM_CLASS}
          onClick={() => {
            setIsMenuOpen(false);
            onOpenDetail?.();
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
            aria-hidden
          >
            <path
              d="M1.167 7s2.042-3.5 5.833-3.5S12.833 7 12.833 7s-2.042 3.5-5.833 3.5S1.167 7 1.167 7z"
              stroke="#222222"
              strokeWidth="1.25"
              strokeLinejoin="round"
            />
            <circle
              cx="7"
              cy="7"
              r="1.75"
              stroke="#222222"
              strokeWidth="1.25"
            />
          </svg>
          <span>{t("Detail")}</span>
        </button>

        <div className={MENU_DIVIDER_CLASS} />
        <span
          className="block w-full"
          title={
            !canCharacterize && characterizeBlockedReason
              ? t(characterizeBlockedReason)
              : undefined
          }
        >
          <button
            type="button"
            className={MENU_ITEM_CLASS}
            disabled={toCharacterization || !canCharacterize}
            onClick={() => {
              if (toCharacterization || !canCharacterize) return;
              setIsMenuOpen(false);
              onOpenCharacterize?.();
            }}
          >
            <ActivityCharacterizeIcon />
            <span>{t("To characterize")}</span>
          </button>
        </span>

        {markAsItems.length > 0 && (
          <>
            <div className={MENU_DIVIDER_CLASS} />
            <div className={MENU_SECTION_CLASS}>{t("Marked as")}</div>
            <div className="pb-0.5">
              {markAsItems.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={MENU_ITEM_CLASS}
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleStatusChange(option.value);
                  }}
                >
                  <span className="text-[#222222]">
                    <CollectionStatusIcon statusIcon={option.statusIcon} />
                  </span>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className={MENU_DIVIDER_CLASS} />
        <div className={MENU_SECTION_CLASS}>{t("Actions")}</div>
        <div className="pb-0.5">
          <button
            type="button"
            className={MENU_ITEM_CLASS}
            onClick={() => {
              setIsMenuOpen(false);
              onOpenCollectionInfo?.();
            }}
          >
            <ActivityCompleteInfoIcon />
            <span>{t("Weight and producer")}</span>
          </button>

          <button
            type="button"
            className={MENU_ITEM_CLASS}
            onClick={() => {
              setIsMenuOpen(false);
              onOpenFiles?.();
            }}
          >
            <ActivityUploadFileIcon />
            <span>{t("Upload file")}</span>
          </button>

          <button
            type="button"
            className={MENU_ITEM_CLASS}
            onClick={() => {
              setIsMenuOpen(false);
              onOpenNotes?.();
            }}
          >
            <NotesIcon />
            <span>{t("Add a comment")}</span>
          </button>

          <button
            type="button"
            className={MENU_ITEM_CLASS}
            onClick={() => {
              setIsMenuOpen(false);
              onOpenTags?.();
            }}
          >
            <ActivityTagIcon />
            <span>{t("Add a tag")}</span>
          </button>
        </div>

        <div className={MENU_DIVIDER_CLASS} />
        <div className={MENU_SECTION_CLASS}>{t("Share")}</div>
        <div className="pb-0.5">
          <button
            type="button"
            className={MENU_ITEM_CLASS}
            onClick={() => {
              setIsMenuOpen(false);
              onRequestPdf?.();
            }}
          >
            <ActivityShareDownloadIcon />
            <span>PDF</span>
          </button>

          <button
            type="button"
            className={MENU_ITEM_CLASS}
            onClick={() => {
              setIsMenuOpen(false);
              onRequestCsv?.();
            }}
          >
            <ActivityShareDownloadIcon />
            <span>CSV</span>
          </button>

          <button
            type="button"
            className={MENU_ITEM_CLASS}
            onClick={() => {
              setIsMenuOpen(false);
              onRequestEmail?.();
            }}
          >
            <ActivityShareEmailIcon />
            <span>{t("Share by email")}</span>
          </button>
        </div>

        <div className={MENU_DIVIDER_CLASS} />
        <button
          type="button"
          className={MENU_ITEM_CLASS}
          onClick={() => {
            setIsMenuOpen(false);
            onOpenContainerSettings?.();
          }}
        >
          <ActivityContainerSettingsIcon />
          <span>
            {t("Settings")} {t("Container")}
          </span>
        </button>
      </div>,
      document.body,
    );

  return (
    <div className="w-full flex justify-between items-center">
      <div className="flex flex-row gap-3 items-center flex-wrap min-w-0">
        <span className="text-[1.575rem] leading-[1.75rem] font-bold text-[#00A7A6]">
          {wasteName}
        </span>
        {!timeLine?.fictiveContainer?.active && (
          <StatusV2Picker
            statusPresentation={statusPresentation}
            menuOptions={markAsItems}
            saving={savingStatus}
            disabled={loading}
            onChange={handleStatusChange}
          />
        )}
      </div>
      <div className="relative flex flex-row gap-2">
        <button
          ref={buttonRef}
          type="button"
          disabled={isDisabled}
          className={`w-10 h-10 rounded-full border-[1.2px] border-gray-300 flex items-center justify-center transition-colors ${menuButtonClass}`}
          onClick={(event) => {
            event.stopPropagation();
            if (isDisabled) return;
            setIsMenuOpen((prev) => !prev);
          }}
          style={{ padding: 0 }}
          aria-label={t("Actions")}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
        >
          <i
            className="fas fa-ellipsis-v text-gray-600"
            style={{
              lineHeight: 1,
              margin: 0,
              padding: 0,
              display: "inline-block",
              transform: "translateY(1.5px)",
            }}
          ></i>
        </button>
      </div>
      {dropdownMenu}
    </div>
  );
};

export default CollectionRowHeader;
